import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ChannelRow {
  readonly channelId: string;
  readonly name: string;
  readonly channelType: string;
  readonly memberCount: number;
  readonly unreadCount: number;
  readonly lastMessageAt: string | null;
}

const TYPES = ['', 'DIRECT', 'GROUP', 'TEAM', 'ANNOUNCEMENT'];

/**
 * GR L27.04 — Chat (`/chat`). The conversation list of collaboration channels
 * (`collab channels`): name, type, member count, unread badge and last activity,
 * each opening the thread view (L27.05). Filterable by channel type.
 */
export function GrChatPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [channelType, setChannelType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (channelType) params.set('channelType', channelType);
    const qs = params.toString();
    apiRequest<{ data: ChannelRow[] }>(`/v1/collab/channels${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, channelType]);

  return (
    <main aria-label="chat">
      <h1>{tr('gr27.chat.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr27.chat.type')}
          <select aria-label="filter-channel-type" value={channelType} onChange={(e) => setChannelType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr27.chat.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr27.chat.loading')}</p> : (
        <>
          <p aria-label="channel-count">{tr('gr27.chat.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr27.chat.empty')}</p> : (
            <ul aria-label="channel-list">
              {rows.map((c) => (
                <li key={c.channelId}>
                  <Link aria-label={`open-${c.channelId}`} to={`/chat/${c.channelId}`}>{c.name}</Link>
                  {' '}<span>{c.channelType}</span>{' · '}<span>{tr('gr27.chat.members', { count: String(c.memberCount) })}</span>
                  {c.unreadCount > 0 && <span aria-label={`unread-${c.channelId}`}>{tr('gr27.chat.unread', { count: String(c.unreadCount) })}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
